import { Link } from "react-router";
import { useBusiness } from "../../lib/business.js";
import { categoryLabel, money, useCosts } from "../costs/api.js";
import { useTemplates } from "../templates/api.js";
import { useDashboard } from "./api.js";
import styles from "./DashboardScreen.module.css";

// Dashboard (doc 01 §2): quick-actions → 4 KPI cards → analytics (volume · mix · SLA)
// → inbox-preview + composer → order log + tier & quality. Numbers scope to the
// active business; empty/"—" states cover the no-Meta-data staging case.
/** "Mon"/"Tue" weekday tick for a YYYY-MM-DD volume bucket. */
const dayLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-MY", { weekday: "short" });

const QUALITY: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  // Meta hasn't rated the number yet (fresh/unconnected) — say so instead of a bare dash.
  unknown: "Not rated yet",
};
const cap = (s: string) => QUALITY[s] ?? s;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export function DashboardScreen() {
  const { activeId } = useBusiness();
  const dash = useDashboard(activeId);
  const costs = useCosts(activeId, 28);
  const templates = useTemplates();
  const d = dash.data;
  const k = d?.kpis;

  const tier = k?.tierLimit ?? null;
  // Tier usage in the governor's unit: business-initiated conversations, rolling 24h.
  const tierUsed = k?.tierUsedToday ?? 0;
  const meterPct = tier && tier > 0 ? Math.min(100, Math.round((tierUsed / tier) * 100)) : 0;
  const sentDelta = (k?.sentToday ?? 0) - (k?.sentYesterday ?? 0);

  const mix = d?.customerMix ?? { new: 0, repeat: 0, vip: 0 };
  const mixTotal = mix.new + mix.repeat + mix.vip;
  const span = Math.max(1, mixTotal);
  const pNew = (mix.new / span) * 100;
  const pRep = (mix.repeat / span) * 100;
  const donut =
    mixTotal === 0
      ? "var(--line)"
      : `conic-gradient(var(--mist-dim) 0 ${pNew}%, var(--azure) ${pNew}% ${pNew + pRep}%, var(--teal) ${pNew + pRep}% 100%)`;
  const pctOf = (n: number) => (mixTotal ? Math.round((n / mixTotal) * 100) : 0);

  // The API's default range is 28 days; the panel is titled "last 7 days" — slice
  // to the actual window so labels, bars, and scale all describe the same thing.
  const volume7 = (d?.volume ?? []).slice(-7);
  const maxVol = Math.max(1, ...volume7.map((v) => Math.max(v.inbound, v.outbound)));
  const sla = d?.sla;
  const approved = (templates.data ?? []).filter((t) => t.status === "approved");
  const wa = costs.data?.whatsapp;

  return (
    <section className={styles.wrap}>
      <header className={styles.head}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.sub}>Overview · today · official WhatsApp Business API</p>
      </header>

      <div className={styles.quickbar}>
        <Link to="/inbox" className={styles.qa}>
          Open inbox
        </Link>
        <Link to="/broadcasts" className={styles.qa}>
          New broadcast
        </Link>
        <Link to="/templates" className={styles.qa}>
          Templates
        </Link>
        <Link to="/campaigns" className={styles.qa}>
          Campaigns
        </Link>
      </div>

      <div className={styles.kpis}>
        <Link to="/broadcasts" className={styles.kpi}>
          <span className={styles.kpiLabel}>Messages sent</span>
          <span className={styles.kpiVal}>{k?.sentToday ?? 0}</span>
          {/* Tier usage is measured in business-initiated CONVERSATIONS (the
              governor's unit) — not raw messages, so the meter uses tierUsedToday. */}
          <span className={styles.meter}>
            <span style={{ width: `${meterPct}%` }} />
          </span>
          <span className={styles.kpiFoot}>
            <span className={styles.kpiRange}>Today</span>
            <span className={styles.kpiDeltaFlat}>
              {sentDelta === 0
                ? "— no change"
                : `${sentDelta < 0 ? "▼" : "▲"} ${Math.abs(sentDelta)} vs yest.`}
            </span>
          </span>
        </Link>

        <Link to="/broadcasts" className={styles.kpi}>
          <span className={styles.kpiLabel}>Delivered</span>
          <span className={`${styles.kpiVal} ${styles.good}`}>
            {k?.deliveredPct != null ? `${k.deliveredPct}%` : "—"}
          </span>
          <span className={styles.kpiFoot}>
            <span className={styles.kpiRange}>Last 28 days</span>
          </span>
        </Link>

        <Link to="/settings" className={styles.kpi}>
          <span className={styles.kpiLabel}>Account health</span>
          <span className={`${styles.kpiVal} ${styles.good}`}>{cap(k?.quality ?? "unknown")}</span>
          <span className={styles.kpiFoot}>
            <span className={styles.kpiRange}>Live</span>
            <span className={k?.metaVerified ? styles.kpiDeltaGood : styles.kpiDeltaFlat}>
              {k?.metaVerified ? "✓ Verified" : "Unverified"}
            </span>
          </span>
        </Link>

        <Link to="/contacts" className={styles.kpi}>
          <span className={styles.kpiLabel}>Opt-in contacts</span>
          <span className={styles.kpiVal}>{(k?.optinTotal ?? 0).toLocaleString()}</span>
          <span className={styles.kpiFoot}>
            <span className={styles.kpiRange}>All-time</span>
            {(k?.optinNew7d ?? 0) > 0 && (
              <span className={styles.kpiDeltaGood}>▲ {k?.optinNew7d} this week</span>
            )}
          </span>
        </Link>
      </div>

      <div className={styles.metricsRow}>
        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>Messages</span>
            <span className={styles.panelMeta}>last 7 days</span>
          </div>
          {volume7.length > 0 ? (
            <div className={styles.bars}>
              {volume7.map((v) => (
                <div
                  key={v.date}
                  className={styles.barGroup}
                  title={`${v.date}: ${v.outbound} sent / ${v.inbound} received`}
                >
                  <div className={styles.barPair}>
                    <span className={styles.barCol}>
                      {v.outbound > 0 && <span className={styles.barVal}>{v.outbound}</span>}
                      <span
                        className={styles.barSent}
                        style={{ height: `${Math.max(2, (v.outbound / maxVol) * 100)}%` }}
                      />
                    </span>
                    <span className={styles.barCol}>
                      {v.inbound > 0 && <span className={styles.barVal}>{v.inbound}</span>}
                      <span
                        className={styles.barRecv}
                        style={{ height: `${Math.max(2, (v.inbound / maxVol) * 100)}%` }}
                      />
                    </span>
                  </div>
                  <span className={styles.barDay}>{dayLabel(v.date)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.empty}>No data.</p>
          )}
          <div className={styles.legend}>
            <span>
              <i className={styles.swSent} /> Sent
            </span>
            <span>
              <i className={styles.swRecv} /> Received
            </span>
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>Customer mix</span>
            <span className={styles.panelMeta}>current · all contacts</span>
          </div>
          <div className={styles.donutRow}>
            <div className={styles.donut} style={{ background: donut }}>
              <div className={styles.donutHole}>
                <b>{mixTotal}</b>
                <span>contacts</span>
              </div>
            </div>
            <ul className={styles.mixLegend}>
              <li>
                <i className={styles.dotNew} /> New <b>{mix.new}</b> <span>{pctOf(mix.new)}%</span>
              </li>
              <li>
                <i className={styles.dotRepeat} /> Repeat buyer <b>{mix.repeat}</b>{" "}
                <span>{pctOf(mix.repeat)}%</span>
              </li>
              <li>
                <i className={styles.dotVip} /> VIP <b>{mix.vip}</b> <span>{pctOf(mix.vip)}%</span>
              </li>
            </ul>
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>First response</span>
            <span className={styles.panelMeta}>median, this week</span>
          </div>
          <div className={styles.slaBody}>
            <div className={styles.slaBig}>
              {sla?.medianMins != null ? sla.medianMins : "—"}
              <span className={styles.slaUnit}>min</span>
            </div>
            {sla?.medianMins != null && sla.prevMins != null && (
              <div className={sla.medianMins <= sla.prevMins ? styles.good : styles.bad}>
                {sla.medianMins <= sla.prevMins ? "▼" : "▲"}{" "}
                {Math.abs(sla.medianMins - sla.prevMins).toFixed(1)} min vs last week
              </div>
            )}
            <div className={styles.slaMeter}>
              <span style={{ width: `${sla?.within15 ?? 0}%` }} />
            </div>
            <div className={styles.slaFoot}>
              {sla?.within15 != null ? `${sla.within15}% within 15 min · ` : ""}
              {(sla?.totalReplies ?? 0).toLocaleString()} first response
              {(sla?.totalReplies ?? 0) === 1 ? "" : "s"}
            </div>
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>Message costs</span>
            <span className={styles.panelMeta}>last 28 days</span>
          </div>
          {wa?.available ? (
            <div className={styles.costBody}>
              <div className={styles.costBig}>
                {money(wa.total, wa.currency)}
                <span className={styles.costUnit}>billed by Meta</span>
              </div>
              <div className={styles.costFoot}>
                {wa.volume.toLocaleString()} billed message{wa.volume === 1 ? "" : "s"}
              </div>
              {wa.byCategory.length > 0 && (
                <ul className={styles.costCats}>
                  {wa.byCategory.slice(0, 3).map((c) => (
                    <li key={c.category}>
                      <span>{categoryLabel(c.category)}</span>
                      <b>{c.cost > 0 ? money(c.cost, wa.currency) : `${c.volume} msg`}</b>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/broadcasts" className={styles.linkBtn}>
                Full breakdown →
              </Link>
            </div>
          ) : (
            <p className={styles.empty}>
              {costs.isLoading
                ? "Loading billing…"
                : "Billing syncs from Meta once this number is connected."}
            </p>
          )}
        </section>
      </div>

      <div className={styles.cols}>
        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>Inbox</span>
            <Link to="/inbox" className={styles.linkBtn}>
              Open inbox →
            </Link>
          </div>
          {d && d.inboxPreview.length > 0 ? (
            d.inboxPreview.map((c) => (
              <Link to="/inbox" key={c.id} className={styles.convMini}>
                <span className={styles.avatar}>{initials(c.name)}</span>
                <span className={styles.convBody}>
                  <span className={styles.convName}>{c.name}</span>
                  <span className={styles.convSnip}>{c.snippet}</span>
                </span>
                {c.unread > 0 && <span className={styles.unread}>{c.unread}</span>}
              </Link>
            ))
          ) : (
            <p className={styles.empty}>No conversations yet.</p>
          )}
        </section>

        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>Broadcast composer</span>
            <Link to="/broadcasts" className={styles.linkBtn}>
              All broadcasts →
            </Link>
          </div>
          <div className={styles.field}>
            <label htmlFor="dash-tpl">Template</label>
            <select id="dash-tpl" disabled={approved.length === 0}>
              {approved.length ? (
                approved.map((t) => <option key={t.id}>{t.name}</option>)
              ) : (
                <option>No approved templates</option>
              )}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="dash-aud">Audience</label>
            <select id="dash-aud">
              <option>Opted-in customers — {(k?.optinTotal ?? 0).toLocaleString()}</option>
            </select>
          </div>
          <div className={styles.composerFoot}>
            <Link to="/broadcasts" className="btn btn--primary">
              Compose
            </Link>
          </div>
        </section>
      </div>

      <div className={styles.cols}>
        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>Order automation log</span>
            <span className={styles.panelMeta}>live events</span>
          </div>
          {d && d.orderLog.length > 0 ? (
            <table className={styles.logTable}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Customer</th>
                  <th>Event</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.orderLog.map((r) => (
                  <tr key={r.id}>
                    <td className={styles.mono}>{r.at}</td>
                    <td className={styles.mono}>{r.customer}</td>
                    <td>{r.event}</td>
                    <td>
                      <span className={`${styles.st} ${styles[r.status] ?? ""}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={styles.empty}>No automation activity yet.</p>
          )}
        </section>

        <section className={styles.panel}>
          <div className={styles.panelH}>
            <span className={styles.panelT}>Tier &amp; quality</span>
            <span className={styles.panelMeta}>this number</span>
          </div>
          <div className={styles.tierq}>
            <div className={styles.tierqRow}>
              <span className={styles.tierqK}>Quality rating</span>
              <span className={styles.tierqV}>{cap(k?.quality ?? "unknown")}</span>
            </div>
            <div className={styles.tierqRow}>
              <span className={styles.tierqK}>Daily tier</span>
              <span className={`${styles.tierqV} ${styles.mono}`}>
                {tier
                  ? `${tier.toLocaleString()} / day`
                  : k?.connected
                    ? "Not assigned yet"
                    : "Not synced yet"}
              </span>
            </div>
            <div className={styles.tierqBarWrap}>
              <span className={styles.tierqBar}>
                <span style={{ width: `${meterPct}%` }} />
              </span>
              <span className={styles.tierqBarMeta}>
                <span>
                  {tierUsed} conversation{tierUsed === 1 ? "" : "s"} used · 24h
                </span>
                {tier != null && <span>{Math.max(0, tier - tierUsed).toLocaleString()} left</span>}
              </span>
            </div>
            {tier == null ? (
              <div className={styles.tierqNext}>
                {k?.connected
                  ? "Meta hasn't assigned a messaging tier to this number yet (test numbers don't get one). It syncs automatically once assigned."
                  : "Tier & quality sync from Meta once this number is connected."}
              </div>
            ) : (
              d?.nextTier && (
                <div className={styles.tierqNext}>
                  ✓{" "}
                  {d.nextTier.unlock === "meta_business_verification"
                    ? "Verify Meta Business"
                    : "Increase volume"}{" "}
                  to raise this number to <b>{d.nextTier.limit.toLocaleString()} / day</b>.
                </div>
              )
            )}
          </div>
        </section>
      </div>
    </section>
  );
}
